/**
 * Phase 7.UI.1 — Stockaily theme primitives.
 *
 * Pure, side-effecting helpers used by:
 *   - the early-hydration <script> in public/index.html (no React yet)
 *   - the ThemeContext (React-side persistence + backend sync)
 *
 * Contract:
 *   • "dark"  = default, premium institutional dark theme.
 *   • "light" = premium institutional light theme.
 *   • Owner Console is locked to dark via OwnerConsoleThemeLock.
 *
 * This module deliberately keeps zero React deps so it can be inlined
 * into the HTML shell to prevent FOUC on first paint.
 */

export const THEME_STORAGE_KEY = "stockaily_theme";
export const VALID_THEMES = ["dark", "light"];
export const DEFAULT_THEME = "dark";

export function isValidTheme(t) {
  return typeof t === "string" && VALID_THEMES.includes(t);
}

/**
 * Read a theme value from localStorage.
 * Defensive — wraps every storage call so SSR / private-mode browsers
 * never throw synchronously.
 */
export function readStoredTheme() {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredTheme(theme) {
  if (!isValidTheme(theme)) return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private-mode or storage-disabled — fail silently */
  }
}

/**
 * Apply a theme to the document by setting `<html data-theme="…">`.
 * Tailwind / shadcn primitives read the CSS variables that are defined
 * per-data-attribute in `index.css`, so this single attribute swaps
 * the entire palette atomically with no class churn.
 *
 * `null` removes the attribute (= falls back to the default :root tokens
 * which encode the dark scheme).
 */
export function applyThemeToDocument(theme) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  if (theme === "light") {
    html.setAttribute("data-theme", "light");
  } else {
    // Dark is the default — explicitly setting the attribute keeps the
    // value introspectable for tests / debugging without changing
    // visual output.
    html.setAttribute("data-theme", "dark");
  }
}

/**
 * Resolution helper. Order of precedence:
 *   1. explicit override (already-validated theme passed in)
 *   2. localStorage value
 *   3. default dark
 *
 * Note: we deliberately do NOT consult `prefers-color-scheme` — the
 * product spec says dark is the global default for new visitors.
 */
export function resolveInitialTheme() {
  return readStoredTheme() || DEFAULT_THEME;
}
